import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-blue";
const Lbl = ({ children }) => <span className="text-sm font-medium text-slate-700 block mb-1.5">{children}</span>;
const Card = ({ title, children, action }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-navy">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

function ItemToolbar({ i, total, onUp, onDown, onDelete, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{label} {i + 1}</span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onUp} disabled={i === 0} data-testid={`move-up-${i}`}
          className="p-1.5 rounded-md text-slate-500 hover:bg-white disabled:opacity-30" title="Move up"><ArrowUp size={15} /></button>
        <button type="button" onClick={onDown} disabled={i === total - 1} data-testid={`move-down-${i}`}
          className="p-1.5 rounded-md text-slate-500 hover:bg-white disabled:opacity-30" title="Move down"><ArrowDown size={15} /></button>
        <button type="button" onClick={onDelete} data-testid={`delete-item-${i}`}
          className="p-1.5 rounded-md text-red-400 hover:bg-red-50" title="Delete"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};
const uid = (p) => `${p}-${Date.now().toString(36)}`;

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/content").then((r) => setC(r.data)); }, []);
  if (!c) return <p className="text-slate-400">Loading…</p>;

  const setHero = (k) => (e) => setC((v) => ({ ...v, hero: { ...v.hero, [k]: e.target.value } }));
  const setAbout = (k) => (e) => setC((v) => ({ ...v, about: { ...v.about, [k]: e.target.value } }));
  const setWhy = (k) => (e) => setC((v) => ({ ...v, why: { ...v.why, [k]: e.target.value } }));
  const setHww = (k) => (e) => setC((v) => ({ ...v, how_we_work: { ...v.how_we_work, [k]: e.target.value } }));
  const setStep = (i, k) => (e) => setC((v) => { const st = [...v.how_we_work.steps]; st[i] = { ...st[i], [k]: e.target.value }; return { ...v, how_we_work: { ...v.how_we_work, steps: st } }; });

  // Services
  const setService = (i, k) => (e) => setC((v) => { const s = [...v.services]; s[i] = { ...s[i], [k]: e.target.value }; return { ...v, services: s }; });
  const addService = () => setC((v) => ({ ...v, services: [...v.services, { id: uid("svc"), title: "New service", category: "Category", icon: "Boxes", desc: "Describe this service." }] }));
  const delService = (i) => setC((v) => ({ ...v, services: v.services.filter((_, x) => x !== i) }));
  const moveService = (i, d) => setC((v) => ({ ...v, services: move(v.services, i, d) }));

  // Projects
  const setProject = (i, k) => (e) => setC((v) => { const p = [...v.projects]; const val = k === "features" ? e.target.value.split("\n").filter(Boolean) : e.target.value; p[i] = { ...p[i], [k]: val }; return { ...v, projects: p }; });
  const addProject = () => setC((v) => ({ ...v, projects: [...v.projects, { id: uid("proj"), name: "New Project", type: "Type", tagline: "Short tagline.", badge: "Category", features: ["Feature one", "Feature two"], image: "/projects/neec-pos.jpg" }] }));
  const delProject = (i) => setC((v) => ({ ...v, projects: v.projects.filter((_, x) => x !== i) }));
  const moveProject = (i, d) => setC((v) => ({ ...v, projects: move(v.projects, i, d) }));

  const setChapter = (i, k) => (e) => setC((v) => { const ch = [...v.why.chapters]; ch[i] = { ...ch[i], [k]: e.target.value }; return { ...v, why: { ...v.why, chapters: ch } }; });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", { data: c }); toast.success("Content saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addBtn = (fn, testid) => (
    <button type="button" onClick={fn} data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-lg border border-brand-blue/30 bg-brand-blue/5 px-3 py-1.5 text-sm font-medium text-brand-blue hover:bg-brand-blue/10">
      <Plus size={15} /> Add
    </button>
  );

  return (
    <div data-testid="admin-content-view" className="max-w-3xl">
      <h2 className="font-display text-2xl font-bold text-navy">Website Content</h2>
      <p className="text-slate-500 text-sm mt-1">Edit the headline, services, featured projects, business types and more.</p>

      <div className="mt-8 space-y-8">
        <Card title="Hero">
          <div><Lbl>Eyebrow</Lbl><input className={inp} value={c.hero.eyebrow || ""} onChange={setHero("eyebrow")} data-testid="content-hero-eyebrow" /></div>
          <div><Lbl>Headline</Lbl><input className={inp} value={c.hero.headline || ""} onChange={setHero("headline")} data-testid="content-hero-headline" /></div>
          <div><Lbl>Subheadline</Lbl><textarea rows={3} className={inp} value={c.hero.subheadline || ""} onChange={setHero("subheadline")} data-testid="content-hero-subheadline" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Lbl>Primary CTA</Lbl><input className={inp} value={c.hero.primary_cta || ""} onChange={setHero("primary_cta")} /></div>
            <div><Lbl>Secondary CTA</Lbl><input className={inp} value={c.hero.secondary_cta || ""} onChange={setHero("secondary_cta")} /></div>
          </div>
        </Card>

        <Card title="Banner & closing">
          <div><Lbl>Marquee banner text</Lbl><textarea rows={2} className={inp} value={c.banner_text || ""} onChange={(e) => setC((v) => ({ ...v, banner_text: e.target.value }))} data-testid="content-banner" /></div>
          <div><Lbl>Closing CTA</Lbl><textarea rows={2} className={inp} value={c.closing_cta || ""} onChange={(e) => setC((v) => ({ ...v, closing_cta: e.target.value }))} data-testid="content-closing" /></div>
        </Card>

        <Card title="Business types served">
          <p className="text-xs text-slate-400 -mt-2">One per line.</p>
          <textarea rows={6} className={inp} value={(c.business_types || []).join("\n")} data-testid="content-business-types"
            onChange={(e) => setC((v) => ({ ...v, business_types: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) }))} />
        </Card>

        <Card title={`Services (${c.services.length})`} action={addBtn(addService, "add-service-button")}>
          <p className="text-xs text-slate-400 -mt-2">Icon uses a Lucide icon name (e.g. Globe, ShoppingCart, Zap, Code2, Smartphone, Video).</p>
          {c.services.map((s, i) => (
            <div key={s.id} className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2" data-testid={`service-editor-${i}`}>
              <ItemToolbar i={i} total={c.services.length} label="Service" onUp={() => moveService(i, -1)} onDown={() => moveService(i, 1)} onDelete={() => delService(i)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Lbl>Title</Lbl><input className={inp} value={s.title} onChange={setService(i, "title")} data-testid={`content-service-title-${i}`} /></div>
                <div><Lbl>Category</Lbl><input className={inp} value={s.category} onChange={setService(i, "category")} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Lbl>Icon</Lbl><input className={inp} value={s.icon} onChange={setService(i, "icon")} placeholder="Globe" /></div>
                <div className="sm:col-span-2"><Lbl>Description</Lbl><input className={inp} value={s.desc} onChange={setService(i, "desc")} /></div>
              </div>
            </div>
          ))}
        </Card>

        <Card title={`Featured projects (${c.projects.length})`} action={addBtn(addProject, "add-project-button")}>
          {c.projects.map((p, i) => (
            <div key={p.id} className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2" data-testid={`project-editor-${i}`}>
              <ItemToolbar i={i} total={c.projects.length} label="Project" onUp={() => moveProject(i, -1)} onDown={() => moveProject(i, 1)} onDelete={() => delProject(i)} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Lbl>Name</Lbl><input className={inp} value={p.name} onChange={setProject(i, "name")} data-testid={`content-project-name-${i}`} /></div>
                <div><Lbl>Type</Lbl><input className={inp} value={p.type} onChange={setProject(i, "type")} /></div>
              </div>
              <div><Lbl>Tagline</Lbl><input className={inp} value={p.tagline} onChange={setProject(i, "tagline")} /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Lbl>Badge</Lbl><input className={inp} value={p.badge} onChange={setProject(i, "badge")} /></div>
                <div><Lbl>Image URL</Lbl><input className={inp} value={p.image} onChange={setProject(i, "image")} /></div>
              </div>
              {p.image && <img src={p.image} alt="preview" className="h-24 w-full object-cover rounded-md border border-slate-200" />}
              <div><Lbl>Features (one per line)</Lbl><textarea rows={3} className={inp} value={(p.features || []).join("\n")} onChange={setProject(i, "features")} /></div>
            </div>
          ))}
        </Card>

        <Card title="About">
          <div><Lbl>Title</Lbl><input className={inp} value={c.about.title || ""} onChange={setAbout("title")} data-testid="content-about-title" /></div>
          <div><Lbl>Body</Lbl><textarea rows={4} className={inp} value={c.about.body || ""} onChange={setAbout("body")} /></div>
          <div><Lbl>Image URL</Lbl><input className={inp} value={c.about.image || ""} onChange={setAbout("image")} /></div>
        </Card>

        <Card title="Why NEEC Digital">
          <div><Lbl>Title</Lbl><input className={inp} value={c.why.title || ""} onChange={setWhy("title")} /></div>
          <div><Lbl>Intro</Lbl><textarea rows={2} className={inp} value={c.why.intro || ""} onChange={setWhy("intro")} /></div>
          {c.why.chapters.map((ch, i) => (
            <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2">
              <div className="grid sm:grid-cols-4 gap-3">
                <div><Lbl>No.</Lbl><input className={inp} value={ch.number} onChange={setChapter(i, "number")} /></div>
                <div className="sm:col-span-3"><Lbl>Title</Lbl><input className={inp} value={ch.title} onChange={setChapter(i, "title")} /></div>
              </div>
              <div><Lbl>Description</Lbl><textarea rows={2} className={inp} value={ch.desc} onChange={setChapter(i, "desc")} /></div>
            </div>
          ))}
        </Card>

        <Card title="How we work">
          <div><Lbl>Title</Lbl><input className={inp} value={c.how_we_work?.title || ""} onChange={setHww("title")} data-testid="content-hww-title" /></div>
          <div><Lbl>Intro</Lbl><textarea rows={2} className={inp} value={c.how_we_work?.intro || ""} onChange={setHww("intro")} /></div>
          {(c.how_we_work?.steps || []).map((st, i) => (
            <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4 space-y-2">
              <div className="grid sm:grid-cols-4 gap-3">
                <div><Lbl>No.</Lbl><input className={inp} value={st.number} onChange={setStep(i, "number")} /></div>
                <div className="sm:col-span-3"><Lbl>Title</Lbl><input className={inp} value={st.title} onChange={setStep(i, "title")} data-testid={`content-hww-step-${i}`} /></div>
              </div>
              <div><Lbl>Description</Lbl><textarea rows={2} className={inp} value={st.desc} onChange={setStep(i, "desc")} /></div>
            </div>
          ))}
        </Card>
      </div>

      <div className="sticky bottom-4 mt-8">        <button onClick={save} disabled={saving} data-testid="content-save-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand-blue px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-500 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save content
        </button>
      </div>
    </div>
  );
}
